<template>
  <RevCard
    v-if="activeDeals && activeDeals.length > 0"
    class="bg-surface-default-hi mood-tangaroa rounded-lg p-24"
  >
    <div class="flex flex-col">
      <div
        class="flex flex-col gap-12 md:flex-row md:flex-wrap md:items-center"
      >
        <IconDealFilled class="text-static-default-hi shrink-0" size="32" />

        <h1 class="text-static-default-hi heading-1">
          {{ i18n(translations.title) }}
        </h1>
        <RevTag
          v-if="activeDeals && activeDeals.length > 0"
          class="mood-main mr-auto md:shrink-0"
          :label="
            i18n(translations.activeDealsTag, { count: activeDeals?.length })
          "
          variant="success"
        />
        <div
          class="order-1 mt-16 flex justify-between gap-16 md:order-none md:ml-auto md:mt-0 md:items-center"
        >
          <RevButton
            class="bg-action-default-hi"
            size="small"
            variant="primary"
            variation="filled"
            @click="handleDownloadDealDetails"
          >
            {{ i18n(translations.downloadDealDetails) }}
          </RevButton>
          <RevButtonIcon
            aria-controls="deals-list"
            :aria-expanded="areDealsExpanded"
            :aria-label="i18n(translations.toggleDealsList)"
            :class="{ 'rotate-180': areDealsExpanded }"
            function="regular"
            :icon="IconChevronDown"
            level="ghost"
            size="medium"
            variant="secondary"
            @click="() => (areDealsExpanded = !areDealsExpanded)"
          />
        </div>
        <div class="flex basis-full flex-wrap gap-8">
          <p class="text-static-default-low body-1">
            {{ i18n(translations.description) }}
          </p>
          <RevLink
            class="mood-inverse body-1-link"
            @click="openDealsInfoDrawer"
          >
            {{ i18n(translations.seeHowItWorks) }}
          </RevLink>
        </div>
      </div>
    </div>
    <ul
      v-show="areDealsExpanded"
      id="deals-list"
      class="mt-24 flex flex-col gap-24"
    >
      <li v-for="deal in activeDeals" :key="deal.name">
        <div class="flex flex-col flex-wrap gap-8 md:flex-row">
          <p class="text-static-default-hi body-1-bold">
            {{ deal.name }}
          </p>
          <div class="flex flex-wrap gap-6">
            <Pill
              v-for="market in deal.markets"
              :key="market"
              customClass="border-static-default-mid border-[1px]"
              size="small"
            >
              <template #default="{ flagSize, textSize }">
                <Flag :country-code="market" :size="flagSize" />
                <span :class="textSize">{{ market }}</span>
              </template>
            </Pill>
          </div>
          <div class="text-static-default-hi body-1 md:ml-auto">
            {{ i18n(translations.dealEndsIn) }}
            <span class="text-static-default-low body-1">
              {{ getDealTimeLabel(deal.timeLeft) }}
            </span>
          </div>
        </div>
      </li>
    </ul>
  </RevCard>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData } from '#app'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconDealFilled } from '@ds/icons/IconDealFilled'

import type { DealCampaign } from '~/api/listings/types'
import { getDifferenceInDays } from '~/utils/date/getDifferenceInDays'

import { MODAL_NAME } from '../DealsInfoDrawer/DealsInfoDrawer.constants'
import Pill from '../ListingsTable/components/Pill/Pill.vue'

import translations from './DealsBanner.translations'

const { openSuccessToast, openErrorToast } = useTheToast()

const areDealsExpanded = ref(false)

const i18n = useI18n()

const $httpFetch = useHttpFetch()
const tracking = useTracking()

const { data: dealsInfo } = useAsyncData('useListingsDealsInfo', () =>
  $httpFetch('/api/seller-experience/listings/active-deal-campaigns'),
)

function openDealsInfoDrawer() {
  openModal(MODAL_NAME)
}

async function handleDownloadDealDetails() {
  try {
    await $httpFetch('/api/seller-experience/listings/export-deals', {
      method: 'POST',
    })

    tracking.trackClick({
      page_type: 'listings',
      zone: 'deals_banner',
      name: 'download deal details',
    })

    openSuccessToast({
      title: i18n(translations.dealDetailsDownloading),
      content: i18n(translations.dealDetailsDownloadingContent),
      actionLabel: i18n(translations.dealDetailsFileLibraryAction),
      actionCallback: () => {
        return navigateTo({ query: { 'file-library': 'true' } })
      },
    })
  } catch (error) {
    const { title: content } = HttpApiError.fromAnyError(error as Error)

    openErrorToast({
      title: i18n(translations.dealDetailsDownloadingError),
      content,
    })
  }
}

const getDealTimeLeft = (deal: DealCampaign) => {
  // Current date
  const startDate = new Date()
  const endDate = new Date(deal.endDate)
  const differenceInDays = getDifferenceInDays(startDate, endDate)

  return Math.round(differenceInDays)
}

const getDealTimeLabel = (numberOfDays: number) => {
  if (numberOfDays === 0) {
    return i18n(translations.dealEndsToday)
  }
  if (numberOfDays === 1) {
    return i18n(translations.dealEndsTomorrow)
  }

  return i18n(translations.daysLeft, { days: numberOfDays })
}

const activeDeals = computed(() =>
  dealsInfo.value
    ?.map((deal) => ({
      ...deal,
      timeLeft: getDealTimeLeft(deal),
    }))
    .filter(
      (deal, index) =>
        deal.timeLeft >= 0 &&
        // Limit to 6 deals
        index <= 5,
    ),
)
</script>
